import { ELLIPSIS_CHARACTER } from '../../../../typography/utils.js';

/** Axis-aligned bounding box for a rect (x, y in pixels, w/h in pixels). */
export type BBox = {
  x: number;
  y: number;
  w: number;
  h: number;
};

/**
 * True when two boxes overlap along the y axis (half-open interval). Used as
 * a cheap filter before checking horizontal occlusion.
 */
export function verticallyOverlaps(a: BBox, b: BBox): boolean {
  return a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * Width of the leftmost horizontal segment of `bbox` that is not covered by
 * any of `blockers` at overlapping y. Returns `0` when the leading edge is
 * itself covered — the caller uses that as a signal to skip the label.
 *
 * `blockers` are treated as later-painted rects, i.e. drawn on top; the
 * caller must supply them in that order (see `RectLabelOverlay`).
 */
export function usableLeftSegmentWidth(bbox: BBox, blockers: BBox[]): number {
  const bboxRight = bbox.x + bbox.w;
  let usableRight = bboxRight;

  for (const blocker of blockers) {
    if (!verticallyOverlaps(bbox, blocker)) {
      continue;
    }
    const blockerRight = blocker.x + blocker.w;
    if (blockerRight <= bbox.x || blocker.x >= bboxRight) {
      continue;
    }
    if (blocker.x <= bbox.x) {
      return 0;
    }
    if (blocker.x < usableRight) {
      usableRight = blocker.x;
    }
  }

  return usableRight - bbox.x;
}

/**
 * Count of characters in `text` that are not the ellipsis. Used to skip labels
 * where truncation would leave only visual noise (`…`, `…A`).
 */
export function countRealChars(text: string): number {
  let count = 0;
  for (const ch of text) {
    if (ch !== ELLIPSIS_CHARACTER) {
      count++;
    }
  }
  return count;
}
